import { ExternalLinkIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useEmbeddedPartner } from 'components/hooks/employer_onboarding/useEmbeddedPartner'
import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Button, ButtonGroup, Checkbox, EmailInput, Modal, PasswordInput, TextLink, usePageTitle } from 'slab'
import { handleGrapeAPIFieldErrors } from 'utils'
import { useGetPartnersEmbeddedOnboardingSessionStatus, usePostPartnersEmbeddedOnboardingMagicLink, usePostPartnersEmbeddedOnboardingSetPassword } from 'utils/web_api'

import { CenteredLayout } from '../CenteredLayout'
import { usePrefill } from '../PrefillProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { emitEmployerOnboardingEvent } from '../utils'

/**
 * EmbeddedRegistrationPage is a specialized registration screen for embedded partner flows.
 * The email is pre-populated (when available) and read-only; the user only needs
 * to set and confirm a password and accept the agreements.
 */

export function EmbeddedRegistrationPage () {
  usePageTitle('Create your password')
  const { navigateToPreviousStep } = useOnboardingStep()
  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not sign up because of an error',
  })
  const { data: partner } = useEmbeddedPartner()
  const { getPrefillValue } = usePrefill()
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [targetOrigins, setTargetOrigins] = useState<string[] | null>(null)
  const form = useForm({
    defaultValues: {
      email: '',
      password: getPrefillValue('password') ?? '',
      password_confirmation: getPrefillValue('password_confirmation') ?? '',
      agree_to_terms: getPrefillValue('agree_to_terms') ?? false,
    },
    mode: 'onTouched',
  })

  const sessionStatusQuery = useGetPartnersEmbeddedOnboardingSessionStatus()
  const magicLinkMutation = usePostPartnersEmbeddedOnboardingMagicLink()
  useEffect(() => {
    // Request a magic link off the bank in case the user has already set their password
    magicLinkMutation.mutate()
  }, [])

  useEffect(() => {
    const data = sessionStatusQuery.data
    // Prefetch email from embedded onboarding partners endpoint
    if (data?.email) form.setValue('email', data.email)

    setTargetOrigins(data?.target_origins || [])

    // If there is already another admin for the session employer, show an error
    if (data?.session_employer_existing_admin_email) {
      setError({ message: `There is already a Thatch account for this business with the email ${data.session_employer_existing_admin_email}. Please log in with that account or ask them to add you as an admin.` })
    }
    if (data?.is_admin_for_another_employer) {
      setError({ message: 'You are already an admin for another employer. Please log in with your account to finish onboarding.' })
    }
  }, [sessionStatusQuery.data])

  useEffect(() => {
    const ml = magicLinkMutation.data?.magic_link
    if (!ml) return

    // If we're not in an iframe, redirect immediately
    if (!isInIframe()) {
      window.location.assign(ml)
    }

    setRedirectUrl(ml)
  }, [magicLinkMutation.data])

  const setPasswordMutation = usePostPartnersEmbeddedOnboardingSetPassword({
    mutation: {
      onSuccess: () => {
        // Get magic link once the password is set
        magicLinkMutation.mutate()
      },
      onError: (error: GrapeAPIErrorType) => {
        if (error.fieldErrors) {
          handleGrapeAPIFieldErrors(error, form)
        } else {
          setError(error)
        }
      },
    },
  })

  const EMPLOYER_AGREEMENT_URL = 'https://thatch.com/legal/employer-agreement'
  const CARDHOLDER_AGREEMENT_URL = 'https://thatch.com/legal/financial-partners/debit-cardholder-agreement'

  // Determine if the embedding partner supports SSO (single source of truth: session status)
  const supportsSSO = Boolean(sessionStatusQuery.data?.supports_sso)

  // Check if the page is running inside an iframe
  const isInIframe = () => {
    try {
      return window.self !== window.top
    } catch {
      // If we can't access window.top due to cross-origin restrictions, we're likely in an iframe
      return true
    }
  }

  // Helper function for showing error toast with support link
  const showErrorToast = () => {
    toast.error(
      <>
        Our team has been notified, but you can{' '}
        <a href='mailto:support@thatch.com' className='underline underline-offset-2'>
          reach out to us
        </a>{' '}
        for help if you need support sooner.
      </>
    )
  }

  const handleCopyAgreementLink = async (
    e: MouseEvent<HTMLAnchorElement>,
    url: string,
    label: string
  ) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(url)
      toast.success(`${label} link copied to clipboard`)
    } catch {
      toast.error('Failed to copy link')
    }
  }

  function onSubmit () {
    const data = form.getValues()
    setPasswordMutation.mutate({
      data: {
        password: data.password,
        password_confirmation: data.password_confirmation,
        agree_to_terms: data.agree_to_terms,
      },
    })
  }

  const partnerName = partner?.friendly_name || partner?.display_name || 'your payroll provider'

  return (
    <CenteredLayout displaySupportLink>
      <Modal
        open={!!redirectUrl && !!targetOrigins?.length && isInIframe()}
        aria-describedby={undefined}
      >
        <Modal.Body>
          <Modal.Title dismissible={false}>Finish onboarding onto Thatch</Modal.Title>
          <div className='mt-4 space-y-4 body'>
            <p>
              You'll be redirected to Thatch's website to finalize your benefits.
            </p>
            <p>
              The settings you configured here will be applied automatically once you create your account.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex flex-col gap-2'>
            <Modal.Button
              variant='primary'
              rightIcon={ExternalLinkIcon}
              disabled={setPasswordMutation.isPending}
              onClick={() => {
                if (!redirectUrl || !targetOrigins || targetOrigins.length === 0) {
                  console.error('Missing redirectUrl or targetOrigins for iframe communication', { redirectUrl, targetOrigins })
                  showErrorToast()
                  return
                }

                emitEmployerOnboardingEvent('employeronboarding_embedded_registration_redirected', {
                  target_origins: targetOrigins,
                })

                const message = {
                  type: 'THATCH_REDIRECT',
                  version: 1,
                  payload: {
                    redirectUrl,
                    expiresAt: null,
                  },
                }
                for (const to of targetOrigins) {
                  window.parent?.postMessage(message, to)
                }
              }}
              width='full'
            >
              Go to thatch.com to finish onboarding
            </Modal.Button>
          </div>
        </Modal.Footer>
      </Modal>
      <CenteredLayout.Header
        heading="Let's finish setting up your account."
        subheading={supportsSSO
          ? `You're connected through ${partnerName}. You'll be able to log in from ${partnerName} or set a Thatch password later.`
          : 'First, create a memorable password.'}
      />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container>
          <EmailInput
            {...form.register('email', { required: true })}
            label='Email'
            description={`We'll automatically use the email you use with ${partnerName}`}
            readOnly
            disabled
          />
          {!supportsSSO && (
            <>
              <PasswordInput
                label='Password'
                {...form.register('password', { required: true, minLength: 8 })}
                error={form.formState.errors.password?.message}
              />
              <PasswordInput
                required
                label='Confirm password'
                {...form.register('password_confirmation', {
                  required: true,
                  validate: (value) => value === form.getValues('password') || 'Passwords do not match',
                })}
                error={form.formState.errors.password_confirmation?.message}
              />
            </>
          )}
          {errorBanner}
        </CenteredLayout.Container>
        <div className='flex flex-col md:flex-row md:justify-between gap-6 md:gap-4 items-center'>
          <Checkbox
            {...form.register('agree_to_terms', { required: true })}
            label={
                 (
                   <>
                     I agree to the{' '}
                     <TextLink
                       href={EMPLOYER_AGREEMENT_URL}
                       onClick={(e) => handleCopyAgreementLink(e, EMPLOYER_AGREEMENT_URL, 'Employer Agreement')}
                     >
                       Employer Agreement
                     </TextLink>{' '}
                     and{' '}
                     <TextLink
                       href={CARDHOLDER_AGREEMENT_URL}
                       onClick={(e) => handleCopyAgreementLink(e, CARDHOLDER_AGREEMENT_URL, 'Debit Cardholder Agreement')}
                     >
                       Debit Cardholder Agreement
                     </TextLink>
                   </>
                  )
            }
          />
          <ButtonGroup align='center' size='large'>
            <Button variant='secondary' onClick={navigateToPreviousStep}>
              Back
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={setPasswordMutation.isPending || sessionStatusQuery.isPending || !form.formState.isValid}
            >
              Create account
            </Button>
          </ButtonGroup>
        </div>
      </form>
    </CenteredLayout>
  )
}
